import React, { useState } from 'react';
import { BookOpen, Download, FileText, Headphones } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';

const BASE = '/documents';
const GUIDES = [
  { id: 'office', title: 'Office Guide: Assigning Patients to Nurses', who: 'For the administrator / office manager', pdf: 'IVORY_Office_Tutorial.pdf',
    pages: ['Welcome and overview', 'Who sees which patients', 'Step 1: Give the nurse a login', 'Step 2: Assign nurses to a patient (every visit type)', 'Step 3: Assign a nurse to a recert visit', 'Step 4: Review and approve the charting', 'Questions and fixes'] },
  { id: 'nurse', title: 'Nurse Guide: Charting Your Visits', who: 'For the nurse doing the visit', pdf: 'IVORY_Nurse_Tutorial.pdf', languages: true,
    pages: ['Welcome and overview', 'Step 1: Sign in for the first time', 'Step 2: Chart and send it back', 'Questions and fixes'] },
];

// narration languages recorded for the Nurse Guide (English is the default recording)
const LANGS = [['', 'English'], ['es', 'Español'], ['zh', '中文 (普通话)'], ['yue', '粵語 (Cantonese)'], ['ru', 'Русский'], ['tl', 'Tagalog'], ['vi', 'Tiếng Việt'], ['ko', '한국어'], ['hy', 'Հայերեն']];

const GuideCard = ({ g }) => {
  const [lang, setLang] = useState('');
  const dir = `${BASE}/tutorial_audio/${g.id}${lang ? `/${lang}` : ''}`;
  const full = lang ? `${dir}/full.mp3` : `${dir}/IVORY_${g.id[0].toUpperCase()}${g.id.slice(1)}_Tutorial_full.mp3`;
  return (
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden" data-testid={`guide-${g.id}`}>
      <div className="bg-gray-900 px-5 py-4">
        <p className="text-xs uppercase tracking-widest text-[#d4af37]">{g.who}</p>
        <h2 className="text-lg font-bold text-white mt-1">{g.title}</h2>
      </div>
      <div className="p-5 space-y-4">
        <div className="flex flex-wrap gap-2">
          <a href={`${BASE}/${g.pdf}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#d4af37] text-gray-900 text-sm font-semibold" data-testid={`guide-${g.id}-pdf`}>
            <FileText className="w-4 h-4" /> Open PDF guide</a>
          <a href={full} download className="flex items-center gap-2 px-4 py-2 rounded-full border text-sm" data-testid={`guide-${g.id}-audio-download`}>
            <Download className="w-4 h-4" /> Download full audio</a>
        </div>
        {g.languages && (
          <label className="flex flex-wrap items-center gap-2 text-sm font-medium text-gray-700">Narration language
            <select value={lang} onChange={e => setLang(e.target.value)} className="border rounded-lg px-3 py-1.5 text-sm" data-testid={`guide-${g.id}-language`}>
              {LANGS.map(([v, n]) => <option key={v} value={v}>{n}</option>)}
            </select>
          </label>
        )}
        <div>
          <p className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-1"><Headphones className="w-4 h-4" /> Listen to the whole guide</p>
          <audio key={full} controls preload="none" src={full} className="w-full" data-testid={`guide-${g.id}-audio-full`} />
        </div>
        <ol className="divide-y border rounded-xl">
          {g.pages.map((title, i) => {
            const src = `${dir}/page_${i + 1}.mp3`;
            return (
              <li key={title} className="p-3 grid gap-2 sm:grid-cols-[1fr_260px_auto] sm:items-center" data-testid={`guide-${g.id}-page-${i + 1}`}>
                <p className="text-sm"><span className="font-semibold text-gray-500 mr-2">Page {i + 1}</span>{title}</p>
                <audio key={src} controls preload="none" src={src} className="w-full h-9" data-testid={`guide-${g.id}-page-${i + 1}-audio`} />
                <a href={src} download className="text-xs text-[#b8941f] underline" data-testid={`guide-${g.id}-page-${i + 1}-download`}>Download</a>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
};

export default function TutorialsPage() {
  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6" data-testid="tutorials-page">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2"><BookOpen className="w-7 h-7 text-[#d4af37]" /> Tutorials</h1>
          <p className="text-sm text-gray-600 mt-1">Picture guides with a spoken narration for every page. Open the PDF, press play, or download the audio to share.</p>
        </div>
        {GUIDES.map(g => <GuideCard key={g.id} g={g} />)}
      </div>
    </DashboardLayout>
  );
}
